// Faz BN — MUI theme builder (token'lardan üretir)
import { createTheme } from "@mui/material/styles";
import { tokensForMode, typography, radius, shadow, motion } from "./tokens";

export function buildMuiTheme(mode = "light") {
  const t = tokensForMode(mode);

  return createTheme({
    palette: {
      mode,
      // 2026-05-14: `soft` alanlari mode-aware soft-fill renkleri tutuyor.
      // Onceki sayfalarda `bgcolor: "warning.soft"` gibi hardcoded acik renkler
      // dark mode'da okunmuyordu. `bgcolor: "warning.soft"` ile theme'den okur.
      primary:    { main: t.primary, dark: t.primaryHover, contrastText: t.primaryFg, soft: t.primarySoft },
      secondary:  { main: t.info, soft: t.infoSoft },
      success:    { main: t.success, soft: t.successSoft },
      warning:    { main: t.warning, soft: t.warningSoft },
      error:      { main: t.danger, soft: t.dangerSoft },
      info:       { main: t.info, soft: t.infoSoft },
      background: { default: t.bg, paper: t.surface, hover: t.bgHover, elev: t.bgElev },
      text:       { primary: t.text, secondary: t.textSecondary, disabled: t.textDisabled, muted: t.textMuted },
      divider:    t.border,
    },

    shape: { borderRadius: radius.md },

    typography: {
      fontFamily: typography.fontFamily,
      h1:       { fontSize: typography.h1.size,    lineHeight: `${typography.h1.line}px`,   fontWeight: typography.h1.weight },
      h2:       { fontSize: typography.h2.size,    lineHeight: `${typography.h2.line}px`,   fontWeight: typography.h2.weight },
      h3:       { fontSize: typography.h3.size,    lineHeight: `${typography.h3.line}px`,   fontWeight: typography.h3.weight },
      h4:       { fontSize: typography.h3.size,    lineHeight: `${typography.h3.line}px`,   fontWeight: typography.h3.weight },
      h5:       { fontSize: typography.h3.size,    lineHeight: `${typography.h3.line}px`,   fontWeight: typography.h3.weight },
      h6:       { fontSize: typography.h3.size,    lineHeight: `${typography.h3.line}px`,   fontWeight: typography.h3.weight },
      body1:    { fontSize: typography.body.size,  lineHeight: `${typography.body.line}px`, fontWeight: typography.body.weight },
      body2:    { fontSize: typography.small.size, lineHeight: `${typography.small.line}px`, fontWeight: typography.small.weight },
      caption:  { fontSize: typography.small.size, lineHeight: `${typography.small.line}px`, fontWeight: typography.small.weight },
      overline: { fontSize: typography.label.size, lineHeight: `${typography.label.line}px`, fontWeight: typography.label.weight, textTransform: "uppercase", letterSpacing: 0.5 },
      button:   { fontWeight: 600, textTransform: "none", letterSpacing: 0 },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: t.bg,
            color: t.text,
            fontVariantNumeric: "tabular-nums",
            transition: `background-color ${motion.base} ${motion.ease}, color ${motion.base} ${motion.ease}`,
          },
          "*:focus-visible": {
            outline: `2px solid ${t.primary}`,
            outlineOffset: 2,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            paddingInline: 16,
            minHeight: 40,
            transition: `background-color ${motion.fast} ${motion.ease}`,
          },
          containedPrimary: {
            "&:hover": { backgroundColor: t.primaryHover },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            transition: `background-color ${motion.fast} ${motion.ease}`,
          },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0, variant: "outlined" },
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            borderColor: t.border,
            backgroundColor: t.surface,
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: "none" },
        },
      },
      MuiTextField: {
        defaultProps: { size: "small", variant: "outlined" },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            "&.Mui-focused": {
              boxShadow: shadow.focus,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontSize: 11,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            color: t.textSecondary,
            backgroundColor: t.bgHover,
            borderBottom: `1px solid ${t.border}`,
          },
          body: {
            fontSize: 14,
            color: t.text,
            borderBottom: `1px solid ${t.border}`,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:hover": { backgroundColor: t.bgHover },
            "&.Mui-selected": { backgroundColor: t.primarySoft },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: radius.full,
            fontWeight: 600,
            fontSize: 11,
            height: 24,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: 12,
            backgroundColor: t.text,
            color: t.surface,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: radius.lg,
          },
        },
      },
    },
  });
}
